import { Box, Button, CircularProgress, Dialog, TextField, Typography } from '@mui/material'
import { useContext, useEffect, useRef, useState } from 'react'
import Context from '../Context'
import theme from '../theme'
import { apiFetch, apiJson } from '../utils/api'
import { useClock } from '../utils/clock'

/**
 * Tickets raised from inside the tool, with the screenshot that explains them. A trader who has just seen
 * something wrong is one keystroke away from a picture of it — Cmd-Shift-4 then paste — and the whole
 * point of this screen is that the picture travels with the words rather than turning up in a chat later
 * with "the search page, this morning, some tennis match".
 */

const FONT = '"Source Sans Pro", -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif'

const MAX_ATTACHMENTS = 5
// what the server will still take after this has shrunk the picture
const MAX_IMAGE_BYTES = 3 * 1024 * 1024
// a screenshot of a 27 inch screen is 5120 wide and nobody needs that to see what went wrong
const MAX_EDGE = 1800
// past this a lossless copy is not worth the megabytes and jpeg earns its artefacts
const HEAVY_BYTES = 1.5 * 1024 * 1024

const fieldSx = {
  backgroundColor: 'white',
  borderRadius: '2px',
  '& .MuiOutlinedInput-input': { fontSize: '14px' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#C9C9D1' }
}

// the same label above a field the settings screen uses, so the two read as one product
const labelSx = { fontFamily: FONT, fontSize: '13px', color: '#6E7378', marginBottom: '6px' }

const cardSx = {
  border: '1px solid #E4E4E8',
  borderRadius: '2px',
  padding: '14px 16px',
  marginBottom: '10px',
  backgroundColor: 'white'
}

const bytesOf = (dataUrl) => Math.round((String(dataUrl).length - String(dataUrl).indexOf(',') - 1) * 0.75)

/**
 * A pasted or picked file, made small enough to travel and to store. Anything already modest is left
 * exactly as it is — re-encoding a 200Kb png only makes it worse.
 */
const toAttachment = (file) => new Promise((resolve, reject) => {
  if (!file.type.startsWith('image/')) {
    reject(new Error(`${file.name || 'that file'} is not an image`))
    return
  }
  const reader = new FileReader()
  reader.onerror = () => reject(new Error('could not read that file'))
  reader.onload = () => {
    const dataUrl = String(reader.result)
    if (file.size <= 400 * 1024) {
      resolve({ name: file.name || 'screenshot.png', dataUrl })
      return
    }
    const image = new Image()
    image.onerror = () => reject(new Error('could not read that image'))
    image.onload = () => {
      const scale = Math.min(1, MAX_EDGE / Math.max(image.width, image.height))
      const canvas = document.createElement('canvas')
      canvas.width = Math.round(image.width * scale)
      canvas.height = Math.round(image.height * scale)
      canvas.getContext('2d').drawImage(image, 0, 0, canvas.width, canvas.height)

      /**
       * Scaled, but still lossless. A screenshot is mostly text and flat colour, which is exactly what
       * png is for and exactly what jpeg ruins — and a window capture carries a transparent corner that
       * jpeg would fill with black. Only when the png is still too heavy to send is jpeg worth its
       * artefacts, and then the picture is a photograph-sized one anyway.
       */
      const asPng = canvas.toDataURL('image/png')
      const chosen = bytesOf(asPng) <= HEAVY_BYTES
        ? { name: file.name || 'screenshot.png', dataUrl: asPng }
        : { name: (file.name || 'screenshot').replace(/\.[^.]+$/, '') + '.jpg', dataUrl: canvas.toDataURL('image/jpeg', 0.85) }

      resolve(bytesOf(chosen.dataUrl) < bytesOf(dataUrl)
        ? chosen
        : { name: file.name || 'screenshot.png', dataUrl })
    }
    image.src = dataUrl
  }
  reader.readAsDataURL(file)
})

const readableSize = (bytes) => (bytes > 1024 * 1024
  ? `${(bytes / 1024 / 1024).toFixed(1)}Mb`
  : `${Math.max(1, Math.round(bytes / 1024))}Kb`)

function SupportPage() {
  const { marketHost } = useContext(Context)
  const clock = useClock()

  const [ tickets, setTickets ] = useState(null)
  const [ error, setError ] = useState('')
  const [ subject, setSubject ] = useState('')
  const [ body, setBody ] = useState('')
  const [ attachments, setAttachments ] = useState([])
  const [ sending, setSending ] = useState(false)
  const [ viewing, setViewing ] = useState(null)
  // which of the two lists is on show; closing a ticket moves it to the other one, which is the point
  const [ showing, setShowing ] = useState('open')
  const fileInput = useRef(null)

  const [ loadFailed, setLoadFailed ] = useState(false)

  const load = async () => {
    try {
      const data = await apiJson(marketHost, '/api/support/tickets')
      setTickets(data.tickets || [])
      setLoadFailed(false)
      setError('')
    } catch (e) {
      // the spinner means "coming"; when it is not coming, say that instead of spinning for ever
      setLoadFailed(true)
      setError(e.message || 'Could not load your tickets')
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line
  }, [])

  const attach = async (files) => {
    setError('')
    const picked = Array.from(files)
    const room = MAX_ATTACHMENTS - attachments.length
    if (room <= 0) {
      setError(`${MAX_ATTACHMENTS} screenshots is the limit for one ticket`)
      return
    }

    /**
     * Every file is judged on its own. A pdf among four screenshots used to lose the screenshots too,
     * and anything past the limit used to vanish without a word — both leave the person wondering
     * which of the pictures they meant to send actually went.
     */
    const added = []
    const refused = []
    for (const file of picked.slice(0, room)) {
      try {
        const attachment = await toAttachment(file)
        if (bytesOf(attachment.dataUrl) > MAX_IMAGE_BYTES) {
          refused.push(`${attachment.name} is still too large after shrinking`)
          continue
        }
        added.push(attachment)
      } catch (e) {
        refused.push(e.message || `${file.name || 'that file'} could not be read`)
      }
    }
    if (picked.length > room) {
      refused.push(`${picked.length - room} left out: ${MAX_ATTACHMENTS} screenshots is the limit`)
    }

    if (added.length) {
      setAttachments((prev) => [...prev, ...added])
    }
    setError(refused.join('. '))
  }

  /**
   * A screenshot on the clipboard is the common case, so the page takes a paste anywhere on it rather
   * than asking anyone to find a button first.
   */
  useEffect(() => {
    const onPaste = (event) => {
      const files = [...(event.clipboardData ? event.clipboardData.items : [])]
        .filter((item) => item.kind === 'file' && item.type.startsWith('image/'))
        .map((item) => item.getAsFile())
        .filter(Boolean)
      if (files.length) {
        event.preventDefault()
        attach(files)
      }
    }
    window.addEventListener('paste', onPaste)
    return () => window.removeEventListener('paste', onPaste)
    // eslint-disable-next-line
  }, [attachments.length])

  const send = async () => {
    if (!subject.trim() || sending) {
      return
    }
    setSending(true)
    setError('')
    try {
      const response = await apiFetch(marketHost, '/api/support/tickets', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subject, body, attachments })
      })
      const data = await response.json()
      if (!response.ok || data.error) {
        throw new Error(data.error || `Request failed with ${response.status}`)
      }
      setSubject('')
      setBody('')
      setAttachments([])
      await load()
    } catch (e) {
      setError(e.message || 'Could not send that')
    } finally {
      setSending(false)
    }
  }

  const setStatus = async (id, status) => {
    // the list is the truth a moment later, but the button has to answer now
    const before = tickets
    setError('')
    setTickets((prev) => prev.map((ticket) => (ticket.id === id
      ? { ...ticket, status, closedAt: status === 'closed' ? Date.now() : null }
      : ticket)))
    try {
      const response = await apiFetch(marketHost, '/api/support/tickets/status', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, status })
      })
      if (!response.ok) {
        throw new Error((await response.json().catch(() => ({}))).error || `Request failed with ${response.status}`)
      }
      load()
    } catch (e) {
      // a card left saying Closed when the server never heard about it is worse than an error
      setTickets(before)
      // the cause is usually a network word like "offline", which on its own says nothing about what
      // happened to the ticket
      setError(`That did not save${e.message ? ` (${e.message})` : ''}. Try again.`)
    }
  }

  const open = (tickets || []).filter((ticket) => ticket.status === 'open')
  const closed = (tickets || []).filter((ticket) => ticket.status !== 'open')
  const shown = showing === 'open' ? open : closed

  const ticketCard = (ticket) => (
    <Box key={ticket.id} sx={cardSx}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '10px' }}>
        <Typography sx={{ flexGrow: 1, minWidth: 0, overflowWrap: 'anywhere', fontFamily: FONT, fontSize: '15px', fontWeight: 700 }}>
          {ticket.subject}
        </Typography>
        <Typography sx={{ fontFamily: FONT, fontSize: '12px', color: '#9A9EA3', whiteSpace: 'nowrap' }}>
          #{ticket.id} · {clock.stamp(ticket.createdAt)}
        </Typography>
      </Box>

      {ticket.body && (
        <Typography sx={{ fontFamily: FONT, fontSize: '14px', color: '#3C4043', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', marginTop: '6px' }}>
          {ticket.body}
        </Typography>
      )}

      {ticket.attachments.length > 0 && (
        <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '10px' }}>
          {ticket.attachments.map((file) => (
            <Box
              key={file.id}
              component="img"
              alt={file.name}
              src={`${marketHost}/api/support/attachment?id=${file.id}`}
              onClick={() => setViewing(file)}
              sx={{
                height: '64px',
                border: '1px solid #E4E4E8',
                borderRadius: '2px',
                cursor: 'zoom-in',
                '&:hover': { borderColor: theme.palette.primary.light }
              }}
            />
          ))}
        </Box>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '12px' }}>
        <Typography sx={{ flexGrow: 1, fontFamily: FONT, fontSize: '12px', color: ticket.status === 'open' ? theme.palette.primary.light : '#9A9EA3' }}>
          {ticket.status === 'open' ? 'Open' : `Closed ${clock.stamp(ticket.closedAt || ticket.createdAt)}`}
        </Typography>
        <Box
          component="button"
          type="button"
          onClick={() => setStatus(ticket.id, ticket.status === 'open' ? 'closed' : 'open')}
          sx={{
            border: '1px solid #D6D6DC', backgroundColor: 'white', borderRadius: '2px',
            padding: '6px 14px', fontFamily: FONT, fontSize: '13px', color: '#6E7378', cursor: 'pointer',
            '&:hover': { borderColor: theme.palette.primary.light, color: theme.palette.primary.light }
          }}
        >
          {ticket.status === 'open' ? 'Close ticket' : 'Reopen'}
        </Box>
      </Box>
    </Box>
  )

  return (
    <Box sx={{ padding: '24px' }}>
      <Box sx={{ backgroundColor: 'white', padding: '24px', maxWidth: '720px' }}>
        <Typography variant="h4" sx={{ marginBottom: '6px', color: theme.palette.primary.main }}>
          New ticket
        </Typography>
        <Typography sx={labelSx}>Subject</Typography>
        <TextField
          fullWidth
          size="small"
          value={subject}
          onChange={(event) => setSubject(event.target.value)}
          placeholder="A one line summary"
          sx={{ ...fieldSx, marginBottom: '16px' }}
          inputProps={{ maxLength: 200 }}
        />

        <Typography sx={labelSx}>Description</Typography>
        <TextField
          fullWidth
          multiline
          minRows={4}
          value={body}
          onChange={(event) => setBody(event.target.value)}
          sx={{ ...fieldSx, marginBottom: '16px' }}
          inputProps={{ maxLength: 8000 }}
        />

        {attachments.length > 0 && (
          <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
            {attachments.map((file, index) => (
              <Box key={index} sx={{ position: 'relative' }}>
                <Box
                  component="img"
                  alt={file.name}
                  src={file.dataUrl}
                  sx={{ height: '72px', border: '1px solid #E4E4E8', borderRadius: '2px' }}
                />
                <Box
                  component="button"
                  type="button"
                  aria-label={`Remove ${file.name}`}
                  onClick={() => setAttachments((prev) => prev.filter((entry, i) => i !== index))}
                  sx={{
                    position: 'absolute', top: '-7px', right: '-7px', width: '20px', height: '20px',
                    border: '1px solid #D6D6DC', borderRadius: '50%', backgroundColor: 'white',
                    cursor: 'pointer', fontSize: '12px', lineHeight: 1, color: '#6E7378', padding: 0,
                    '&:hover': { color: theme.palette.secondary.main, borderColor: theme.palette.secondary.main }
                  }}
                >
                  ×
                </Box>
                <Typography sx={{ fontFamily: FONT, fontSize: '10px', color: '#9A9EA3', textAlign: 'center' }}>
                  {readableSize(bytesOf(file.dataUrl))}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Button
            onClick={send}
            disabled={!subject.trim() || sending}
            sx={{
              height: '40px', padding: '0 28px', fontSize: '15px', letterSpacing: '0.06em',
              color: '#ffffff', backgroundColor: theme.palette.secondary.main, borderRadius: '2px',
              '&:hover': { backgroundColor: '#A81819' },
              '&.Mui-disabled': { backgroundColor: '#DCDCE2', color: '#9A9EA3' }
            }}
          >
            {sending ? 'Submitting…' : 'Submit'}
          </Button>

          <Box
            component="button"
            type="button"
            onClick={() => fileInput.current && fileInput.current.click()}
            sx={{
              height: '40px', border: '1px solid #D6D6DC', backgroundColor: 'white', borderRadius: '2px',
              padding: '0 18px', fontFamily: FONT, fontSize: '13px', color: '#6E7378', cursor: 'pointer',
              '&:hover': { borderColor: theme.palette.primary.light }
            }}
          >
            Attach screenshot
          </Box>
          <Box
            component="input"
            type="file"
            accept="image/*"
            multiple
            ref={fileInput}
            onChange={(event) => {
              attach(event.target.files)
              event.target.value = ''
            }}
            sx={{ display: 'none' }}
          />

          {error && (
            <Typography sx={{ fontFamily: FONT, fontSize: '13px', color: theme.palette.secondary.main }}>
              {error}
            </Typography>
          )}
        </Box>

        {/* the same slanted plates the event screen uses for its periods, so tabs look like tabs here too */}
        <Box sx={{ display: 'flex', margin: '28px 0 14px' }}>
          {[
            { key: 'open', label: 'Open' },
            { key: 'closed', label: 'Closed' }
          ].map((tab, index) => {
            const active = tab.key === showing
            return (
              <Typography
                key={tab.key}
                variant="h6"
                onClick={() => setShowing(tab.key)}
                sx={{
                  cursor: 'pointer',
                  userSelect: 'none',
                  padding: '5px 16px',
                  whiteSpace: 'nowrap',
                  marginLeft: index === 0 ? 0 : '-8px',
                  color: active ? 'white' : '#6E7378',
                  backgroundColor: active ? theme.palette.primary.light : '#DCDCE2',
                  clipPath: 'polygon(10px 0, 100% 0, calc(100% - 10px) 100%, 0 100%)',
                  '&:hover': { backgroundColor: active ? theme.palette.primary.light : '#CFCFD6' }
                }}
              >
                {tab.label}
              </Typography>
            )
          })}
        </Box>

        {tickets === null && !loadFailed && <CircularProgress size={22} />}
        {tickets === null && loadFailed && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Typography sx={{ fontFamily: FONT, fontSize: '13px', color: theme.palette.secondary.main }}>
              Your tickets could not be loaded.
            </Typography>
            <Box
              component="button"
              type="button"
              onClick={() => { setLoadFailed(false); load() }}
              sx={{
                border: '1px solid #D6D6DC', backgroundColor: 'white', borderRadius: '2px',
                padding: '5px 14px', fontFamily: FONT, fontSize: '13px', color: '#6E7378', cursor: 'pointer',
                '&:hover': { borderColor: theme.palette.primary.light }
              }}
            >
              Try again
            </Box>
          </Box>
        )}
        {tickets !== null && shown.length === 0 && (
          <Typography sx={{ fontFamily: FONT, fontSize: '13px', color: '#9A9EA3' }}>
            {showing === 'open' ? 'Nothing open.' : 'Nothing closed yet.'}
          </Typography>
        )}
        {shown.map(ticketCard)}
      </Box>

      {/* a screenshot is worth looking at properly, and the thumbnails are 64 pixels tall */}
      <Dialog open={Boolean(viewing)} onClose={() => setViewing(null)} maxWidth="lg">
        {viewing && (
          <Box
            component="img"
            alt={viewing.name}
            src={`${marketHost}/api/support/attachment?id=${viewing.id}`}
            onClick={() => setViewing(null)}
            sx={{ display: 'block', maxWidth: '92vw', maxHeight: '88vh', cursor: 'zoom-out' }}
          />
        )}
      </Dialog>
    </Box>
  )
}

export default SupportPage
